import React from "react";
import { FiltersTypes } from "@common/types/filters/general";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { BaseFilterProps } from "../../lib";
import { TriStateBooleanFilter } from "../TriStateBooleanFilter/TriStateBooleanFilter";
import styles from "./Eligible1099Filter.module.scss";

const eligibleOption: FilterMultiselectOption = {
    value: "eligible",
    label: "Eligible",
    searchableLabel: "eligible",
};

const notEligibleOption: FilterMultiselectOption = {
    value: "not_eligible",
    label: "Not eligible",
    searchableLabel: "not eligible",
};

type IncludeOption =
    | "excludeCreditCard"
    | "excludePaymentProcessor"
    | "includeExcludedAmounts";

const includeOptions: Array<FilterMultiselectOption<IncludeOption>> = [
    {
        value: "excludeCreditCard",
        label: "Include credit card transactions",
        searchableLabel: "include credit card transactions",
        tooltip:
            "Credit card payments are reported on 1099-K by the card network, not on the 1099-NEC you file.",
    },
    {
        value: "excludePaymentProcessor",
        label: "Include payment processor transactions",
        searchableLabel: "include payment processor transactions",
        tooltip:
            "Payments through a processor like Stripe or PayPal are reported on 1099-K by the processor, not on the 1099-NEC you file.",
    },
    {
        value: "includeExcludedAmounts",
        label: "Include excluded amounts",
        searchableLabel: "include excluded amounts",
    },
];

const isIncluded = (filters: FiltersTypes, option: IncludeOption) => {
    if (option === "excludeCreditCard") {
        return filters.shouldExcludeCreditCardJournalEntries !== true;
    }
    if (option === "excludePaymentProcessor") {
        return filters.shouldExcludePaymentProcessorJournalEntries !== true;
    }
    return filters.includeExcludedAmounts !== false;
};

const toggledFilters = (
    filters: FiltersTypes,
    option: IncludeOption,
): Partial<FiltersTypes> => {
    if (option === "excludeCreditCard") {
        return {
            shouldExcludeCreditCardJournalEntries:
                filters.shouldExcludeCreditCardJournalEntries === true
                    ? undefined
                    : true,
        };
    }
    if (option === "excludePaymentProcessor") {
        return {
            shouldExcludePaymentProcessorJournalEntries:
                filters.shouldExcludePaymentProcessorJournalEntries === true
                    ? undefined
                    : true,
        };
    }
    return {
        includeExcludedAmounts:
            filters.includeExcludedAmounts === false ? undefined : false,
    };
};

export const Eligible1099Filter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const includedValues = includeOptions
        .map((option) => option.value)
        .filter((option) => isIncluded(filters, option));

    return (
        <div className={styles.container}>
            <TriStateBooleanFilter
                value={filters.isEligible1099}
                onChange={(isEligible1099) => onChange({ isEligible1099 })}
                trueOption={eligibleOption}
                falseOption={notEligibleOption}
                name="isEligible1099"
            />
            <div className={styles.section}>
                <p className={styles.sectionLabel}>Options</p>
                <FilterMultiselect
                    inDropdown={false}
                    searchable={false}
                    options={includeOptions}
                    value={includedValues}
                    onChange={(nextValues) => {
                        const toggled = includeOptions.find(
                            (option) =>
                                nextValues.includes(option.value) !==
                                includedValues.includes(option.value),
                        );
                        if (toggled) {
                            onChange(toggledFilters(filters, toggled.value));
                        }
                    }}
                />
            </div>
        </div>
    );
};
